import { ReactPlugin } from '@microsoft/applicationinsights-react-js'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import { isCanceledDependency } from './aiCancelFilter'
import { DependencyStatusCarrier, dependencyStatus } from './aiDependencyStatus'

export const appInsightsReact = new ReactPlugin()
export const appInsights = new ApplicationInsights({
    config: {
        connectionString:
            'InstrumentationKey=417924ef-4814-4e9a-880b-69516abc7d57;IngestionEndpoint=https://westeurope-5.in.applicationinsights.azure.com/;LiveEndpoint=https://westeurope.livediagnostics.monitor.azure.com/;ApplicationId=50e2ffce-054d-4094-927f-027fc4b27e89',
        extensions: [appInsightsReact],
        enableAutoRouteTracking: true,
        autoTrackPageVisitTime: true,
    },
})

// One-off cleanup (August 2026): setAuthenticatedUserContext used to run with
// storeInCookie enabled, so existing users still carry an ai_authUser cookie holding a
// possibly outdated host. Clear it before loadAppInsights — after that the SDK has already
// read it back in and stamps the first pageView with the stale tenant. The identity is set
// per page load from AuthorizationContext instead (see InsightsContainer in pages/App.tsx).
// Safe to remove once all clients have run this for a month or so.
// Leave ai_user and ai_session alone: they feed user_Id and session_Id.
// Guarded: this module is imported from unit tests, which run in the node environment.
if (typeof document !== 'undefined') {
    document.cookie = 'ai_authUser=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT'
}

if (process.env.BOOTSTRAP_ENV !== 'dev') {
    appInsights.loadAppInsights()
}
appInsights.addTelemetryInitializer(envelope => !isCanceledDependency(envelope))
// After the cancel filter: it matches on the original host-qualified name.
appInsights.addTelemetryInitializer(envelope => rewriteDependencyName(envelope))
// After the rewrite: it matches on the placeholder names that rewrite produces.
appInsights.addTelemetryInitializer(envelope => markExpectedGraphFailure(envelope))
appInsights.addTelemetryInitializer(envelope => !isNoiseException(envelope))

// Exceptions we can never act on: browser-internal ResizeObserver noise and
// cross-origin "Script error." events that carry no stack at all.
const NOISE_EXCEPTION_MESSAGES = [
    'ResizeObserver loop completed with undelivered notifications.',
    'ResizeObserver loop limit exceeded',
    'Script error.',
]

export function isNoiseException(envelope: {
    baseType?: string
    baseData?: { exceptions?: { message?: string; typeName?: string; hasFullStack?: boolean }[] }
}): boolean {
    if (envelope?.baseType !== 'ExceptionData') {
        return false
    }
    const exceptions = envelope.baseData?.exceptions
    if (!exceptions?.length) {
        return false
    }
    // Depending on how the browser surfaces the error, the text sits in message, in typeName, or is
    // split across both ("ErrorEvent" + the message); match on the pair so neither shape slips through.
    return exceptions.every(exception => {
        const text = [exception.typeName, exception.message].filter(Boolean).join(' ')
        return NOISE_EXCEPTION_MESSAGES.some(noise => text.includes(noise))
    })
}

// Application Insights names auto-tracked XHR/fetch dependencies
// "METHOD scheme://host/path", so the portal's Browser dependency views
// split the same API call into one row per host. Rewriting the name to
// "METHOD /path" (with id-like segments collapsed to {id}/{guid}) groups
// them by route; the host stays available in the dependency's target
// field and the full URL in its data field.
export function rewriteDependencyName(envelope: { baseType?: string; baseData?: { name?: string; data?: string } }): void {
    if (envelope?.baseType !== 'RemoteDependencyData') {
        return
    }
    const data = envelope.baseData
    if (!data?.name) {
        return
    }
    data.name = toPathOnlyName(data.name)
    // The data field is what the portal shows as the called URL, so it has to lose
    // the same personal parts as the name; keep its host when it carries one.
    if (data.data) {
        data.data = sanitizeUrl(data.data)
    }
}

// Only the path of a parsed URL is used, so any origin works as the base a relative
// name needs; window is absent when these helpers run outside the browser (tests).
function parseBase(): string {
    return typeof window === 'undefined' ? 'http://localhost' : window.location.origin
}

export function toPathOnlyName(name: string): string {
    const spaceIndex = name.indexOf(' ')
    const method = spaceIndex > 0 ? name.slice(0, spaceIndex) : ''
    const url = spaceIndex > 0 ? name.slice(spaceIndex + 1) : name
    let path: string
    try {
        path = normalizePathSegments(new URL(url, parseBase()).pathname)
    } catch {
        return name
    }
    return method ? `${method} ${path}` : path
}

export function sanitizeUrl(value: string): string {
    const spaceIndex = value.indexOf(' ')
    const method = spaceIndex > 0 ? value.slice(0, spaceIndex) : ''
    const url = spaceIndex > 0 ? value.slice(spaceIndex + 1) : value
    let parsed: URL
    try {
        parsed = new URL(url, parseBase())
    } catch {
        return value
    }
    const path = normalizePathSegments(parsed.pathname)
    const origin = url.includes('://') ? parsed.origin : ''
    // The query is kept: it says what the call asked for and none of the parts we
    // collapse live there.
    return [method, `${origin}${path}${parsed.search}`].filter(Boolean).join(' ')
}

const GUID_SEGMENT = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
// Microsoft Graph puts variable parts straight in the path: a SharePoint drive id
// and the mailbox owner's e-mail address. Both make every call a one-off URL, and
// the e-mail address is a personal detail we should not be storing in telemetry.
const DRIVE_ID_SEGMENT = /^b![A-Za-z0-9_-]+$/
const EMAIL_SEGMENT = /^[^@/]+@[^@/]+\.[^@/]+$/

function normalizePathSegments(path: string): string {
    return collapseDriveItemPath(path)
        .split('/')
        .map(segment => {
            if (/^\d+$/.test(segment)) {
                return '{id}'
            }
            if (GUID_SEGMENT.test(segment)) {
                return '{guid}'
            }
            if (DRIVE_ID_SEGMENT.test(segment)) {
                return '{drive-id}'
            }
            if (EMAIL_SEGMENT.test(decodeSegment(segment))) {
                return '{email}'
            }
            return segment
        })
        .join('/')
}

// A stray percent sign makes decodeURIComponent throw, and a throwing initializer
// takes the whole telemetry chain down with it; the raw segment is good enough then.
function decodeSegment(segment: string): string {
    try {
        return decodeURIComponent(segment)
    } catch {
        return segment
    }
}

// Graph addresses a drive item by its literal folder path — for us the dossier
// name — in ".../root:/<folders>", optionally followed by an action such as
// ":/children". The folder path names a client's dossier, so it is collapsed
// whole; the action after it is kept because it says what the call did.
const DRIVE_ITEM_PATH_MARKER = '/root:/'

function collapseDriveItemPath(path: string): string {
    const start = path.indexOf(DRIVE_ITEM_PATH_MARKER)
    if (start < 0) {
        return path
    }
    const rest = path.slice(start + DRIVE_ITEM_PATH_MARKER.length)
    const actionIndex = rest.lastIndexOf(':/')
    const action = actionIndex >= 0 ? rest.slice(actionIndex) : ''
    return `${path.slice(0, start)}${DRIVE_ITEM_PATH_MARKER}{path}${action}`
}

// Two Graph calls fail as part of their normal use. The "does this folder exist"
// probe answers 404 for every dossier folder that still has to be created, and
// translateExchangeIds answers 403 on the Outlook versions and shared mailboxes
// that do not allow it — both are handled by the caller. Left as failures they
// fill the failure views; counting them as successful keeps the call itself
// visible while a real outage on the same endpoint (throttling, gateway or
// server error) still stands out.
const EXPECTED_GRAPH_FAILURES = [
    { name: '/v1.0/drives/{drive-id}/root:/{path}', resultCode: '404' },
    { name: '/translateExchangeIds', resultCode: '403' },
]

// Shared with the request-failure telemetry (helpers/requestTelemetry.ts), which skips the
// same two calls: what is not worth a failure row is not worth an event either. Both sides
// pass a name that toPathOnlyName has already collapsed to placeholders.
export function isExpectedGraphFailure(name?: string, resultCode?: string | number): boolean {
    const collapsedName = name ?? ''
    const status = String(resultCode ?? '')
    return EXPECTED_GRAPH_FAILURES.some(expected => collapsedName.includes(expected.name) && status === expected.resultCode)
}

export function markExpectedGraphFailure(envelope: {
    baseType?: string
    baseData?: DependencyStatusCarrier & { name?: string; success?: boolean }
    data?: Record<string, unknown>
}): void {
    if (envelope?.baseType !== 'RemoteDependencyData') {
        return
    }
    const data = envelope.baseData
    if (!data || data.success !== false) {
        return
    }
    if (!isExpectedGraphFailure(data.name, dependencyStatus(data))) {
        return
    }
    data.success = true
    if (envelope.data) {
        // Keeps the outcome findable: the result code stays 404/403, this says we expected it.
        envelope.data.expectedFailure = true
    }
}
